import { FC, ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Info } from 'lucide-react';
import classnames from 'classnames';
import useDiscountExclusivity, { DiscountKind } from './useDiscountExclusivity';
import useDiscountDisplacement from './useDiscountDisplacement';
import useActiveDiscount from './useActiveDiscount';
import { selectActiveOffers, selectAppliedOffers } from '../../store/slices/OfferSlice';
import { selectCachedOrder, selectHasCachedOrderPointsRedemption } from '../../store/slices/OrderSlice';
import usePromoCode from '../../hooks/order/usePromoCode';
import useGiftCard from '../gift-cards/hooks/useGiftCard';
import useOffersTranslations from '../offers/hooks/useOffersTranslations';
import useGetBasketOfferHandlerByKind from '../offers/hooks/get-offer-handler/useGetBasketOfferHandlerByKind';
import useBasket from '../../hooks/basket/useBasket';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { COUPON_TYPES } from '../../services/exports/Constants';

interface Props {
  className?: string;
}

/**
 * DisplacementNotice — rendered in the cart drawer and checkout (PROD-407, PM video / Lovable
 * prototype). Only one of {offer, promo, gift, points} applies per order; when one displaces
 * another, this shows an entity-named message + Undo, in BOTH directions:
 *
 *  - **Forward** (derived from live state): a promo code, gift card or points redemption is
 *    currently suppressing an available offer — *"Promo code CHEVERE applied. This removed the
 *    2-für-1 offer. [Undo]"*. Undo removes that discount so the offer re-applies via
 *    `useAutoApplyBestOffer`.
 *  - **Reverse** (from the `discountDisplacement` record set at "Use offer instead"): an offer
 *    displaced a promo/gift/points — *"Offer 2-für-1 applied. This removed the CHEVERE promo
 *    code. [Undo]"*. Undo restores that discount (which re-suppresses the offer — the customer
 *    "switching back").
 *
 * Visibility-only: it never mutates offer state directly; the backend still owns exclusivity.
 */
const DiscountDisplacementNotice: FC<Props> = ({ className }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Features:DiscountExclusivity.displacement' });
  const { offerBlockedBy } = useDiscountExclusivity();
  const activeOffers = useSelector(selectActiveOffers);
  const appliedOffers = useSelector(selectAppliedOffers);
  const cachedOrder = useSelector(selectCachedOrder);
  const { getLabelByOffer } = useOffersTranslations();
  // The active promo/gift's real € saving. `cached_order.total_discount` alone is unreliable here
  // (0 for a fixed-amount promo whose value lives in `coupon_details.components` — that's why the
  // notice showed "Promo code TEST (0,00 €)" while the Discount row read −4,00). useActiveDiscount
  // resolves it the same way the points hint does; keep total_discount only as a fallback.
  const activeDiscount = useActiveDiscount();
  const { removePromoCode } = usePromoCode();
  const { giftCard, remove: removeGiftCard } = useGiftCard();
  const {
    displaced,
    clear,
    revert,
    displacedPoints,
    clearPointsDisplacement,
    revertPoints,
    pointsDisplacedCoupon,
    clearPointsDisplacedCoupon,
    revertPointsDisplacedCoupon,
  } = useDiscountDisplacement();

  const hasOffer = (appliedOffers?.length ?? 0) > 0;
  const hasPromo = cachedOrder?.coupon_type === COUPON_TYPES.PROMO_CODE;
  const hasGift = !!giftCard;
  const hasPoints = useSelector(selectHasCachedOrderPointsRedemption);

  // The forward notice must only claim it "removed an offer" when an offer would ACTUALLY discount
  // the current basket if the promo/gift weren't there. We key off the offer's `profit` for the
  // basket — NOT `isUnlocked` (BOGO/half-price handlers hardcode isUnlocked:true so a deal is always
  // "available"), and NOT `unlockedOffers` (the suppression engine clears it while a promo/gift is
  // active). An empty basket, or items that don't meet the offer's requirement, yields profit 0 →
  // nothing is displaced → no notice; otherwise we name the offer that actually applies.
  const basket = useBasket();
  const basketItems = basket?.items ?? [];
  const getOfferHandler = useGetBasketOfferHandlerByKind();
  const { formatCurrency } = useAppMethods();
  // The offers that actually discount this basket (would be lost to the promo/gift), highest-profit
  // first — so we name the offer really lost, and can say "removed {n} offers" when several apply.
  const suppressedOffers = (activeOffers ?? [])
    .map((offer) => ({ offer, profit: getOfferHandler(offer, basketItems)?.profit ?? 0 }))
    .filter((entry) => entry.profit > 0)
    .sort((a, b) => b.profit - a.profit);
  const suppressedOffer = suppressedOffers[0]?.offer;
  const suppressedTotal = suppressedOffers.reduce((sum, e) => sum + e.profit, 0);

  // The reverse record is stale once a blocker is active again (the customer switched back —
  // the forward notice takes over) — drop it so it can't resurface later.
  useEffect(() => {
    if (displaced && offerBlockedBy) {
      clear();
    }
  }, [displaced, offerBlockedBy, clear]);

  // The points-displacement record is stale once the points are back (Undo) or the promo/gift that
  // displaced them is gone — drop it so the notice can't resurface later.
  useEffect(() => {
    if (displacedPoints && (hasPoints || (!hasPromo && !hasGift))) {
      clearPointsDisplacement();
    }
  }, [displacedPoints, hasPoints, hasPromo, hasGift, clearPointsDisplacement]);

  // The points-displaced-a-coupon record is stale once the points are gone or a promo/gift is active
  // again (the customer switched back) — drop it so the notice can't resurface later.
  useEffect(() => {
    if (pointsDisplacedCoupon && (!hasPoints || hasPromo || hasGift)) {
      clearPointsDisplacedCoupon();
    }
  }, [pointsDisplacedCoupon, hasPoints, hasPromo, hasGift, clearPointsDisplacedCoupon]);

  // --- Forward: a promo code or gift card is currently suppressing an offer that WOULD apply. ---
  // (An offer and points coexist, so points never suppress an offer — CTO-confirmed 2026-08-03.)
  if (offerBlockedBy && suppressedOffer) {
    const appliedKind: Extract<DiscountKind, 'promo' | 'gift'> = offerBlockedBy;
    const onUndo = () => (appliedKind === 'promo' ? removePromoCode() : removeGiftCard());

    // Several offers displaced at once (item 6) — name the top two + the total, not just one.
    const message =
      suppressedOffers.length > 1
        ? t(`offer_removed_multi.${appliedKind}`, {
            code: cachedOrder?.coupon_details?.code ?? '',
            codeValue: formatCurrency(activeDiscount?.saving ?? cachedOrder?.total_discount ?? 0),
            count: suppressedOffers.length,
            first: getLabelByOffer(suppressedOffers[0].offer),
            second: getLabelByOffer(suppressedOffers[1].offer),
            total: formatCurrency(suppressedTotal),
          })
        : t('message', {
            applied:
              appliedKind === 'promo'
                ? t('applied.promo', { code: cachedOrder?.coupon_details?.code ?? '' })
                : t('applied.gift'),
            removed: t('removed.offer', { name: getLabelByOffer(suppressedOffer) }),
          });

    return (
      <Notice
        appliedKind={appliedKind}
        removedKind="offer"
        message={message}
        undoLabel={t('undo')}
        onUndo={onUndo}
        className={className}
      />
    );
  }

  // --- A promo code or gift card displaced a points redemption. ---
  // (Recorded at the apply gesture; shown while that promo/gift is active and the points are gone.)
  if (displacedPoints && (hasPromo || hasGift) && !hasPoints) {
    const appliedLabel =
      displacedPoints.kind === 'gift' ? t('applied.gift') : t('applied.promo', { code: displacedPoints.label });

    return (
      <Notice
        appliedKind={displacedPoints.kind}
        removedKind="points"
        message={t('message', {
          applied: appliedLabel,
          removed: t('removed.points'),
        })}
        undoLabel={t('undo')}
        onUndo={revertPoints}
        className={className}
      />
    );
  }

  // --- A points redemption displaced a promo code / gift card (UX feedback item 3). ---
  // (Recorded at the "use points" gesture; shown while points are active and the code is gone.)
  if (pointsDisplacedCoupon && hasPoints && !hasPromo && !hasGift) {
    const { kind, points, pointsSaving, couponSaving, label } = pointsDisplacedCoupon;
    const key = couponSaving != null ? `points_displaced.${kind}` : `points_displaced.${kind}_no_saving`;

    return (
      <Notice
        appliedKind="points"
        removedKind={kind}
        message={t(key, { points, pointsSaving, couponSaving, name: label })}
        undoLabel={t('undo')}
        onUndo={revertPointsDisplacedCoupon}
        className={className}
      />
    );
  }

  // --- Reverse: an offer displaced a promo code or gift card via "Use offer instead". ---
  // (Offers coexist with points, so an offer never displaces a points redemption.)
  if (displaced && hasOffer && !offerBlockedBy) {
    const removedLabel = {
      promo: () => t('removed.promo', { code: displaced.label }),
      gift: () => t('removed.gift'),
    }[displaced.kind]();

    return (
      <Notice
        appliedKind="offer"
        removedKind={displaced.kind}
        message={t('message', {
          applied: t('applied.offer', { name: getLabelByOffer(appliedOffers[0]) }),
          removed: removedLabel,
        })}
        undoLabel={t('undo')}
        onUndo={revert}
        className={className}
      />
    );
  }

  return null;
};

interface NoticeProps {
  appliedKind: DiscountKind;
  removedKind: DiscountKind;
  message: ReactNode;
  undoLabel: ReactNode;
  onUndo: () => void;
  className?: string;
}

const Notice: FC<NoticeProps> = ({ appliedKind, removedKind, message, undoLabel, onUndo, className }) => (
  <div
    data-test="displacement-notice"
    data-applied={appliedKind}
    data-removed={removedKind}
    className={classnames(
      'flex items-start gap-xs rounded-lg border-xs border-warning/40 bg-warning/5 p-sm',
      className,
    )}
  >
    <Info className="w-4 h-4 shrink-0 text-warning mt-[1px]" />
    <div className="flex-1 min-w-0 space-y-2xs">
      <p className="text-xs text-brand-text-primary">{message}</p>
      <button
        type="button"
        onClick={onUndo}
        data-test="displacement-undo"
        className="text-xs font-semibold text-brand-primary hover:underline"
      >
        {undoLabel}
      </button>
    </div>
  </div>
);

export default DiscountDisplacementNotice;
